import { encryptPassword } from "../utils/encryption";
import { apiRequest } from "./api";
import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";

// Base URL for login/logout API endpoints
const AUTH_API_BASE_URL = process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT || 'https://api.qa.construction.digitaleye.ai/api';

// Auth-specific API request function
const authApiRequest = async (endpoint, options = {}) => {
  const token = tokenManager.getToken();
  const { headers: optionHeaders = {}, ...restOptions } = options;

  const config = {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      
      ...optionHeaders,
    },
  };

  const fullUrl = `${AUTH_API_BASE_URL}${endpoint}`;
  console.log("Making Auth API request:", {
    url: fullUrl,
    endpoint,
    hasAuthHeader: !!config.headers.Authorization,
  });

  try {
    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Auth API request failed:", {
        status: response.status,
        error: errorData,
      });
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return;
      }
      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error("Auth API request failed:", error);
    throw error;
  }
};

/**
 * Authentication service for handling login, logout, and registration
 */
export const authService = {
  /**
   * Send OTP to email
   * @param {string} email - User email
   * @returns {Promise} OTP response
   */
  sendOtp: async ({ email }) => {
    try {
      const response = await authApiRequest("/auth/sendOtp", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify OTP
   * @param {string} email - User email
   * @param {string} otp - OTP code
   * @returns {Promise} Verification response
   */
  verifyOtp: async ({ email, otp }) => {
    try {
      const response = await authApiRequest("/auth/verifyOtp", {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Reset Password
   * @param {string} email - User email
   * @param {string} newPassword - New password
   * @param {string} confirmNewPassword - Confirm new password
   * @param {string} passwordResetToken - Password reset token
   * @param {string} otp - OTP code
   * @returns {Promise} Reset password response
   */
  resetPassword: async ({
    email,
    newPassword,
    confirmNewPassword,
    passwordResetToken,
    otp,
  }) => {
    try {
      const response = await authApiRequest("/auth/resetPassword", {
        method: "POST",
        body: JSON.stringify({
          email,
          newPassword,
          confirmNewPassword,
          passwordResetToken,
          otp,
        }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login response with user data and token
   */
  login: async (email, password) => {
    try {
      const encryptedPassword = encryptPassword(password);
      const response = await authApiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password: encryptedPassword }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  refreshUser: async (token) => {
    try {
      const response = await authApiRequest("/auth/refreshUser", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  /**
   * Register new user
   * @param {string} name - User name
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Registration response
   */
  register: async (name, email, password) => {
    try {
      const response = await authApiRequest("/auth/register", {
        method: "POST",
        body: JSON.stringify({ name, email, password }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout current user
   * @returns {Promise} Logout response
   */
  logout: async () => {
    try {
      const response = await authApiRequest("/auth/logout", {
        method: "POST",
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get current user profile
   * @returns {Promise} User profile data
   */
  getProfile: async () => {
    try {
      const response = await authApiRequest("/auth/profile");
      return response;
    } catch (error) {
      throw error;
    }
  },
};
